import { useEffect, useState } from "react";
import { Plus, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { RoleBadge, Btn, Modal, Field, inputCls } from "../components";

const AdminUsers = () => {
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "director" });
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/users").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/users", form);
      toast.success(`Account created for ${form.email} — share the password with them directly`);
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", role: "director" });
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const patch = async (u, body, msg) => {
    try {
      await adminApi.patch(`/users/${u.id}`, body);
      toast.success(msg);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.patch(`/users/${resetFor.id}`, { password: newPw });
      toast.success(`Password reset for ${resetFor.email}`);
      setResetFor(null);
      setNewPw("");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Users & Roles</h1>
          <p className="mt-1 text-sm text-slate-500">There is exactly one Super Admin. New staff accounts are created as Director or HR.</p>
        </div>
        <Btn onClick={() => setCreateOpen(true)} data-testid="add-user-btn"><Plus size={14} /> Create Account</Btn>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Name", "Email", "Role", "Status", "Last Login", "Actions"].map((h) => (
                <th key={h} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : (
              items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-6 py-3 text-slate-600">{u.email}</td>
                  <td className="px-6 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${u.disabled ? "text-red-600" : "text-emerald-600"}`}>{u.disabled ? "Disabled" : "Active"}</span>
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500">{u.last_login ? fmtDate(u.last_login) : "Never"}</td>
                  <td className="px-6 py-3">
                    {u.role === "super_admin" ? (
                      <span className="text-[11px] text-slate-400 italic">Protected account</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          value={u.role}
                          onChange={(e) => patch(u, { role: e.target.value }, `Role changed to ${e.target.value === "hr" ? "HR" : "Director"}`)}
                          className="border border-slate-300 text-xs px-2 py-1.5 rounded-sm"
                          data-testid={`user-role-${u.id}`}
                        >
                          <option value="director">Director</option>
                          <option value="hr">HR</option>
                        </select>
                        <button onClick={() => setResetFor(u)} className="p-2 text-slate-400 hover:text-[#C5221F]" title="Reset password" data-testid={`user-reset-${u.id}`}><KeyRound size={14} /></button>
                        <Btn
                          variant={u.disabled ? "dark" : "danger"}
                          className="!px-2.5 !py-1.5"
                          onClick={() => patch(u, { disabled: !u.disabled }, u.disabled ? "Account enabled" : "Account disabled")}
                          data-testid={`user-toggle-${u.id}`}
                        >
                          {u.disabled ? "Enable" : "Disable"}
                        </Btn>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Staff Account">
        <form onSubmit={create} className="space-y-4">
          <Field label="Full Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} data-testid="new-user-name" /></Field>
          <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-user-email" /></Field>
          <Field label="Temporary Password" hint="Minimum 10 characters. The user can change it in Settings.">
            <input className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={10} data-testid="new-user-password" />
          </Field>
          <Field label="Role">
            <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
              <option value="director">Director — projects, pages, media, enquiries</option>
              <option value="hr">HR — careers & job openings, enquiries</option>
            </select>
          </Field>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="create-user-submit">{busy && <Loader2 size={13} className="animate-spin" />} Create Account</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!resetFor} onClose={() => setResetFor(null)} title={`Reset Password — ${resetFor?.email || ""}`}>
        <form onSubmit={resetPw} className="space-y-4">
          <Field label="New Password" hint="Minimum 10 characters.">
            <input className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={10} data-testid="reset-password-input" />
          </Field>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setResetFor(null)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="reset-password-submit">Reset Password</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
